/**
 * The append-only, hash-chained audit log.
 *
 * BFH VI R 64/04 (16.11.2005) allows an electronic record only where later
 * changes are either technically impossible or "in ihrer Reichweite in der
 * Datei selbst dokumentiert und offen gelegt". BFH VI B 37/23 (12.01.2024)
 * adds that the history must be apparent on ordinary inspection of the file,
 * not via a separate query to an administrator.
 *
 * So: every mutation appends an entry holding the full before and after
 * snapshots plus a field-level diff, each entry carries the SHA-256 of its
 * predecessor, and the whole chain is written into every export and rendered
 * inline in the record view. Editing the underlying IndexedDB directly breaks
 * the chain at a detectable point.
 *
 * See `01_Input/rechtliche-anforderungen.md` section 3.
 */

import Dexie from 'dexie';
import { sealJson, openJson, sha256Hex } from '@/crypto/primitives';
import {
  GENESIS_HASH,
  type AuditAction,
  type AuditEntityType,
  type AuditEntry,
  type FieldChange,
} from '@/domain/types';
import { de } from '@/i18n/de';
import { db, PAYLOAD_VERSION, monthBucket, type AuditEnvelope } from './schema';

/**
 * Deterministic JSON: object keys sorted, no incidental whitespace.
 *
 * The hash is only meaningful if two runs over the same data produce the same
 * bytes, and `JSON.stringify` preserves insertion order rather than sorting.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
}

/** The exact preimage the chain hash is computed over. */
export function auditPreimage(entry: Omit<AuditEntry, 'hash'>): string {
  return canonicalJson({
    seq: entry.seq,
    id: entry.id,
    at: entry.at,
    driverId: entry.driverId,
    action: entry.action,
    entityType: entry.entityType,
    entityId: entry.entityId,
    entityLabel: entry.entityLabel,
    changes: entry.changes,
    before: entry.before ?? null,
    after: entry.after ?? null,
    reason: entry.reason ?? null,
    prevHash: entry.prevHash,
  });
}

export async function computeAuditHash(entry: Omit<AuditEntry, 'hash'>): Promise<string> {
  return sha256Hex(auditPreimage(entry));
}

export interface AuditContext {
  dataKey: CryptoKey;
  driverId: string;
}

export interface AppendAuditInput {
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  entityLabel: string;
  changes?: FieldChange[];
  before?: unknown;
  after?: unknown;
  reason?: string;
  /** Override the timestamp; only used when restoring a backup. */
  at?: string;
}

/**
 * Append one entry. Callers must already be inside a Dexie transaction that
 * also covers whatever table they are mutating, so a record change and its
 * audit entry commit together or not at all.
 *
 * The two crypto steps go through `Dexie.waitFor`, which keeps the surrounding
 * transaction alive across a promise Dexie did not create. Call this function
 * directly rather than wrapping the whole thing in `waitFor` — a `waitFor`
 * parks the transaction, so the table reads and writes below would then sit
 * behind a barrier that only lifts once they have finished, and deadlock.
 */
export async function appendAudit(
  ctx: AuditContext,
  input: AppendAuditInput,
): Promise<AuditEntry> {
  const last = await db.auditLog.orderBy('seq').last();
  const seq = (last?.seq ?? 0) + 1;
  const prevHash = last?.hash ?? GENESIS_HASH;

  const withoutHash: Omit<AuditEntry, 'hash'> = {
    seq,
    id: crypto.randomUUID(),
    at: input.at ?? new Date().toISOString(),
    driverId: ctx.driverId,
    action: input.action,
    entityType: input.entityType,
    entityId: input.entityId,
    entityLabel: input.entityLabel,
    changes: input.changes ?? [],
    before: input.before,
    after: input.after,
    reason: input.reason,
    prevHash,
  };

  const hash = await Dexie.waitFor(computeAuditHash(withoutHash));
  const entry: AuditEntry = { ...withoutHash, hash };
  const sealed = await Dexie.waitFor(sealJson(ctx.dataKey, entry));

  const envelope: AuditEnvelope = {
    id: entry.id,
    bucket: monthBucket(entry.at.slice(0, 10)),
    iv: sealed.iv,
    ct: sealed.ct,
    pv: PAYLOAD_VERSION,
    seq: entry.seq,
    prevHash: entry.prevHash,
    hash: entry.hash,
    at: entry.at,
    entityType: entry.entityType,
    entityId: entry.entityId,
  };
  await db.auditLog.add(envelope);
  return entry;
}

export async function readAuditEntry(
  dataKey: CryptoKey,
  envelope: AuditEnvelope,
): Promise<AuditEntry> {
  return openJson<AuditEntry>(dataKey, { iv: envelope.iv, ct: envelope.ct });
}

/** Full history for one record, oldest first, for inline rendering. */
export async function auditForEntity(
  dataKey: CryptoKey,
  entityId: string,
): Promise<AuditEntry[]> {
  const envelopes = await db.auditLog.where('entityId').equals(entityId).toArray();
  envelopes.sort((a, b) => a.seq - b.seq);
  return Promise.all(envelopes.map((e) => readAuditEntry(dataKey, e)));
}

export async function auditInRange(
  dataKey: CryptoKey,
  fromIso: string,
  toIsoExclusive: string,
): Promise<AuditEntry[]> {
  const envelopes = await db.auditLog.where('at').between(fromIso, toIsoExclusive, true, false).toArray();
  envelopes.sort((a, b) => a.seq - b.seq);
  return Promise.all(envelopes.map((e) => readAuditEntry(dataKey, e)));
}

// ---------------------------------------------------------------------------
// Verification
// ---------------------------------------------------------------------------

export type ChainProblem =
  | { kind: 'gap'; expectedSeq: number; foundSeq: number }
  | { kind: 'broken-link'; seq: number; expectedPrevHash: string; foundPrevHash: string }
  | { kind: 'bad-hash'; seq: number; storedHash: string; computedHash: string }
  | { kind: 'undecryptable'; seq: number }
  | { kind: 'envelope-mismatch'; seq: number; field: string };

export interface ChainVerification {
  ok: boolean;
  entryCount: number;
  problems: ChainProblem[];
  /** Hash of the newest entry — the value to quote in an export footer. */
  headHash: string;
  verifiedAt: string;
}

/**
 * Walk the whole chain and prove it has not been tampered with.
 *
 * This runs on every export and on demand from the settings screen. It is
 * deliberately exhaustive rather than sampled: the point of the chain is that
 * a single altered byte anywhere is detectable, and a sampled check would
 * forfeit that.
 */
export async function verifyChain(dataKey: CryptoKey): Promise<ChainVerification> {
  const envelopes = await db.auditLog.orderBy('seq').toArray();
  const problems: ChainProblem[] = [];
  let prevHash = GENESIS_HASH;
  let expectedSeq = 1;
  let headHash = GENESIS_HASH;

  for (const envelope of envelopes) {
    if (envelope.seq !== expectedSeq) {
      problems.push({ kind: 'gap', expectedSeq, foundSeq: envelope.seq });
      expectedSeq = envelope.seq;
    }

    let entry: AuditEntry;
    try {
      entry = await readAuditEntry(dataKey, envelope);
    } catch {
      problems.push({ kind: 'undecryptable', seq: envelope.seq });
      prevHash = envelope.hash;
      headHash = envelope.hash;
      expectedSeq += 1;
      continue;
    }

    // The clear-text columns exist for indexing; if they disagree with the
    // sealed payload, someone edited the row outside the app.
    if (entry.hash !== envelope.hash) {
      problems.push({ kind: 'envelope-mismatch', seq: envelope.seq, field: 'hash' });
    }
    if (entry.prevHash !== envelope.prevHash) {
      problems.push({ kind: 'envelope-mismatch', seq: envelope.seq, field: 'prevHash' });
    }
    if (entry.entityId !== envelope.entityId) {
      problems.push({ kind: 'envelope-mismatch', seq: envelope.seq, field: 'entityId' });
    }

    if (entry.prevHash !== prevHash) {
      problems.push({
        kind: 'broken-link',
        seq: entry.seq,
        expectedPrevHash: prevHash,
        foundPrevHash: entry.prevHash,
      });
    }

    const { hash: storedHash, ...rest } = entry;
    const computedHash = await computeAuditHash(rest);
    if (computedHash !== storedHash) {
      problems.push({ kind: 'bad-hash', seq: entry.seq, storedHash, computedHash });
    }

    prevHash = storedHash;
    headHash = storedHash;
    expectedSeq += 1;
  }

  return {
    ok: problems.length === 0,
    entryCount: envelopes.length,
    problems,
    headHash,
    verifiedAt: new Date().toISOString(),
  };
}

export function describeChainProblem(problem: ChainProblem): string {
  switch (problem.kind) {
    case 'gap':
      return `Lücke in der Protokollkette: Eintrag ${problem.expectedSeq} fehlt, gefunden wurde ${problem.foundSeq}.`;
    case 'broken-link':
      return `Kette bei Eintrag ${problem.seq} unterbrochen — der Vorgänger-Hash stimmt nicht.`;
    case 'bad-hash':
      return `Eintrag ${problem.seq} wurde nachträglich verändert — der Inhalt passt nicht zum Hash.`;
    case 'undecryptable':
      return `Eintrag ${problem.seq} lässt sich nicht entschlüsseln.`;
    case 'envelope-mismatch':
      return `Eintrag ${problem.seq}: das Feld "${problem.field}" weicht vom versiegelten Inhalt ab.`;
  }
}

// ---------------------------------------------------------------------------
// Diffing
// ---------------------------------------------------------------------------

/**
 * German labels for the fields an auditor will read.
 *
 * Always German, whatever the interface language: the label written here goes
 * into the SHA-256 preimage and into every export, so it has to be stable.
 * `AuditTrail` re-looks-up the current language by `field` for display only.
 */
export const FIELD_LABELS: Record<string, string> = de.audit.fields;

/** Fields that change on every save and would drown the real diff. */
const IGNORED_FIELDS = new Set(['useCount', 'lastUsedAt', 'createdAt', 'id']);

function stringifyValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'object') return canonicalJson(value);
  return String(value);
}

export function diffRecords(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown>,
): FieldChange[] {
  const changes: FieldChange[] = [];
  const keys = new Set([...Object.keys(before ?? {}), ...Object.keys(after)]);

  for (const key of keys) {
    if (IGNORED_FIELDS.has(key)) continue;
    const beforeValue = stringifyValue(before?.[key]);
    const afterValue = stringifyValue(after[key]);
    if (beforeValue === afterValue) continue;
    changes.push({
      field: key,
      label: FIELD_LABELS[key] ?? key,
      before: beforeValue,
      after: afterValue,
    });
  }

  return changes.sort((a, b) => a.label.localeCompare(b.label, 'de'));
}
